import { observeOn } from 'rxjs/operators';
import { Observable, queueScheduler, observable, from } from 'rxjs';
import { InjectionToken, Injectable, Optional, Inject, NgZone, Pipe, NgModule } from '@angular/core';
import { __assign, __extends } from 'tslib';
import { ApolloClient } from 'apollo-client';

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,uselessCode} checked by tsc
 */
/**
 * @template T
 * @param {?} promiseFn
 * @return {?}
 */
function fromPromise(promiseFn) {
    return new Observable(function (subscriber) {
        promiseFn().then(function (result) {
            if (!subscriber.closed) {
                subscriber.next(result);
                subscriber.complete();
            }
        }, function (error) {
            if (!subscriber.closed) {
                subscriber.error(error);
            }
        });
        return function () { return subscriber.unsubscribe(); };
    });
}
var ZoneScheduler = /** @class */ (function () {
    function ZoneScheduler(zone) {
        this.zone = zone;
        this.now = Date.now ? Date.now : function () { return +new Date(); };
    }
    /**
     * @template T
     * @param {?} work
     * @param {?=} delay
     * @param {?=} state
     * @return {?}
     */
    ZoneScheduler.prototype.schedule = /**
     * @template T
     * @param {?} work
     * @param {?=} delay
     * @param {?=} state
     * @return {?}
     */
    function (work, delay, state) {
        if (delay === void 0) { delay = 0; }
        return (/** @type {?} */ (this.zone.run(function () {
            return queueScheduler.schedule(work, delay, state);
        })));
    };
    return ZoneScheduler;
}());
// XXX: Apollo's QueryObservable is not compatible with RxJS
// TODO: remove it in one of future releases
// https://github.com/ReactiveX/rxjs/blob/9fb0ce9e09c865920cf37915cc675e3b3a75050b/src/internal/util/subscribeTo.ts#L32
/**
 * @template T
 * @param {?} obs
 * @return {?}
 */
function fixObservable(obs) {
    ((/** @type {?} */ (obs)))[observable] = function () { return obs; };
    return obs;
}
/**
 * @template T
 * @param {?} obs
 * @param {?} ngZone
 * @return {?}
 */
function wrapWithZone(obs, ngZone) {
    return obs.pipe(observeOn(new ZoneScheduler(ngZone)));
}

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,uselessCode} checked by tsc
 */
/**
 * @template T, V
 */
var  /**
 * @template T, V
 */
QueryRef = /** @class */ (function () {
    function QueryRef(obsQuery, ngZone) {
        this.obsQuery = obsQuery;
        this.valueChanges = wrapWithZone(from(fixObservable(this.obsQuery)), ngZone);
        this.queryId = this.obsQuery.queryId;
    }
    // ObservableQuery's methods
    // ObservableQuery's methods
    /**
     * @return {?}
     */
    QueryRef.prototype.result = 
    // ObservableQuery's methods
    /**
     * @return {?}
     */
    function () {
        return this.obsQuery.result();
    };
    /**
     * @return {?}
     */
    QueryRef.prototype.currentResult = /**
     * @return {?}
     */
    function () {
        return this.obsQuery.currentResult();
    };
    /**
     * @return {?}
     */
    QueryRef.prototype.getLastResult = /**
     * @return {?}
     */
    function () {
        return this.obsQuery.getLastResult();
    };
    /**
     * @return {?}
     */
    QueryRef.prototype.getLastError = /**
     * @return {?}
     */
    function () {
        return this.obsQuery.getLastError();
    };
    /**
     * @return {?}
     */
    QueryRef.prototype.resetLastResults = /**
     * @return {?}
     */
    function () {
        return this.obsQuery.resetLastResults();
    };
    /**
     * @param {?=} variables
     * @return {?}
     */
    QueryRef.prototype.refetch = /**
     * @param {?=} variables
     * @return {?}
     */
    function (variables) {
        return this.obsQuery.refetch(variables);
    };
    /**
     * @template K
     * @param {?} fetchMoreOptions
     * @return {?}
     */
    QueryRef.prototype.fetchMore = /**
     * @template K
     * @param {?} fetchMoreOptions
     * @return {?}
     */
    function (fetchMoreOptions) {
        return this.obsQuery.fetchMore(fetchMoreOptions);
    };
    /**
     * @template MT, MV
     * @param {?} options
     * @return {?}
     */
    QueryRef.prototype.subscribeToMore = /**
     * @template MT, MV
     * @param {?} options
     * @return {?}
     */
    function (options) {
        // XXX: there's a bug in apollo-client typings
        // it should not inherit types from ObservableQuery
        return this.obsQuery.subscribeToMore((/** @type {?} */ (options)));
    };
    /**
     * @param {?} mapFn
     * @return {?}
     */
    QueryRef.prototype.updateQuery = /**
     * @param {?} mapFn
     * @return {?}
     */
    function (mapFn) {
        return this.obsQuery.updateQuery(mapFn);
    };
    /**
     * @return {?}
     */
    QueryRef.prototype.stopPolling = /**
     * @return {?}
     */
    function () {
        return this.obsQuery.stopPolling();
    };
    /**
     * @param {?} pollInterval
     * @return {?}
     */
    QueryRef.prototype.startPolling = /**
     * @param {?} pollInterval
     * @return {?}
     */
    function (pollInterval) {
        return this.obsQuery.startPolling(pollInterval);
    };
    /**
     * @param {?} opts
     * @return {?}
     */
    QueryRef.prototype.setOptions = /**
     * @param {?} opts
     * @return {?}
     */
    function (opts) {
        return this.obsQuery.setOptions(opts);
    };
    /**
     * @param {?} variables
     * @param {?=} tryFetch
     * @param {?=} fetchResults
     * @return {?}
     */
    QueryRef.prototype.setVariables = /**
     * @param {?} variables
     * @param {?=} tryFetch
     * @param {?=} fetchResults
     * @return {?}
     */
    function (variables, tryFetch, fetchResults) {
        if (tryFetch === void 0) { tryFetch = false; }
        if (fetchResults === void 0) { fetchResults = true; }
        return this.obsQuery.setVariables(variables, tryFetch, fetchResults);
    };
    return QueryRef;
}());

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,uselessCode} checked by tsc
 */
/** @type {?} */
var APOLLO_OPTIONS = new InjectionToken('[apollo-angular] options');

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,uselessCode} checked by tsc
 */
/**
 * @template TCacheShape
 */
var  /**
 * @template TCacheShape
 */
ApolloBase = /** @class */ (function () {
    function ApolloBase(ngZone, _client) {
        this.ngZone = ngZone;
        this._client = _client;
    }
    /**
     * @template T, V
     * @param {?} options
     * @return {?}
     */
    ApolloBase.prototype.watchQuery = /**
     * @template T, V
     * @param {?} options
     * @return {?}
     */
    function (options) {
        return new QueryRef((/** @type {?} */ (this.client.watchQuery(__assign({}, options)))), this.ngZone);
    };
    /**
     * @template T, V
     * @param {?} options
     * @return {?}
     */
    ApolloBase.prototype.query = /**
     * @template T, V
     * @param {?} options
     * @return {?}
     */
    function (options) {
        var _this = this;
        return fromPromise(function () {
            return _this.client.query(__assign({}, options));
        });
    };
    /**
     * @template T, V
     * @param {?} options
     * @return {?}
     */
    ApolloBase.prototype.mutate = /**
     * @template T, V
     * @param {?} options
     * @return {?}
     */
    function (options) {
        var _this = this;
        return fromPromise(function () {
            return _this.client.mutate(__assign({}, options));
        });
    };
    /**
     * @template T, V
     * @param {?} options
     * @param {?=} extra
     * @return {?}
     */
    ApolloBase.prototype.subscribe = /**
     * @template T, V
     * @param {?} options
     * @param {?=} extra
     * @return {?}
     */
    function (options, extra) {
        /** @type {?} */
        var obs = from(fixObservable(this.client.subscribe(__assign({}, options))));
        return extra && extra.useZone !== true
            ? obs
            : wrapWithZone(obs, this.ngZone);
    };
    /**
     * @return {?}
     */
    ApolloBase.prototype.getClient = /**
     * @return {?}
     */
    function () {
        return this._client;
    };
    /**
     * @param {?} client
     * @return {?}
     */
    ApolloBase.prototype.setClient = /**
     * @param {?} client
     * @return {?}
     */
    function (client) {
        if (this._client) {
            throw new Error('Client has been already defined');
        }
        this._client = client;
    };
    Object.defineProperty(ApolloBase.prototype, "client", {
        get: /**
         * @return {?}
         */
        function () {
            this.beforeEach();
            return this._client;
        },
        enumerable: true,
        configurable: true
    });
    /**
     * @return {?}
     */
    ApolloBase.prototype.beforeEach = /**
     * @return {?}
     */
    function () {
        this.checkInstance();
    };
    /**
     * @return {?}
     */
    ApolloBase.prototype.checkInstance = /**
     * @return {?}
     */
    function () {
        if (!this._client) {
            throw new Error('Client has not been defined yet');
        }
    };
    return ApolloBase;
}());
var Apollo = /** @class */ (function (_super) {
    __extends(Apollo, _super);
    function Apollo(_ngZone, apolloOptions) {
        var _this = _super.call(this, _ngZone) || this;
        _this._ngZone = _ngZone;
        _this.map = new Map();
        if (apolloOptions) {
            _this.createDefault(apolloOptions);
        }
        return _this;
    }
    /**
     * @template TCacheShape
     * @param {?} options
     * @param {?=} name
     * @return {?}
     */
    Apollo.prototype.create = /**
     * @template TCacheShape
     * @param {?} options
     * @param {?=} name
     * @return {?}
     */
    function (options, name) {
        if (name && name !== 'default') {
            this.createNamed(name, options);
        }
        else {
            this.createDefault(options);
        }
    };
    /**
     * @return {?}
     */
    Apollo.prototype.default = /**
     * @return {?}
     */
    function () {
        return this;
    };
    /**
     * @param {?} name
     * @return {?}
     */
    Apollo.prototype.use = /**
     * @param {?} name
     * @return {?}
     */
    function (name) {
        if (name === 'default') {
            return this.default();
        }
        return this.map.get(name);
    };
    /**
     * @template TCacheShape
     * @param {?} options
     * @return {?}
     */
    Apollo.prototype.createDefault = /**
     * @template TCacheShape
     * @param {?} options
     * @return {?}
     */
    function (options) {
        if (this.getClient()) {
            throw new Error('Apollo has been already created.');
        }
        return this.setClient(new ApolloClient(options));
    };
    /**
     * @template TCacheShape
     * @param {?} name
     * @param {?} options
     * @return {?}
     */
    Apollo.prototype.createNamed = /**
     * @template TCacheShape
     * @param {?} name
     * @param {?} options
     * @return {?}
     */
    function (name, options) {
        if (this.map.has(name)) {
            throw new Error("Client " + name + " has been already created");
        }
        this.map.set(name, new ApolloBase(this._ngZone, new ApolloClient(options)));
    };
    Apollo.decorators = [
        { type: Injectable }
    ];
    /** @nocollapse */
    Apollo.ctorParameters = function () { return [
        { type: NgZone },
        { type: undefined, decorators: [{ type: Optional }, { type: Inject, args: [APOLLO_OPTIONS,] }] }
    ]; };
    return Apollo;
}(ApolloBase));

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,uselessCode} checked by tsc
 */
/**
 * @template T, V
 */
var Query = /** @class */ (function () {
    function Query(apollo) {
        this.apollo = apollo;
        this.client = 'default';
    }
    /**
     * @param {?=} variables
     * @param {?=} options
     * @return {?}
     */
    Query.prototype.watch = /**
     * @param {?=} variables
     * @param {?=} options
     * @return {?}
     */
    function (variables, options) {
        return this.apollo.use(this.client).watchQuery(__assign({}, options, { variables: variables, query: this.document }));
    };
    /**
     * @param {?=} variables
     * @param {?=} options
     * @return {?}
     */
    Query.prototype.fetch = /**
     * @param {?=} variables
     * @param {?=} options
     * @return {?}
     */
    function (variables, options) {
        return this.apollo.use(this.client).query(__assign({}, options, { variables: variables, query: this.document }));
    };
    Query.decorators = [
        { type: Injectable }
    ];
    /** @nocollapse */
    Query.ctorParameters = function () { return [
        { type: Apollo }
    ]; };
    return Query;
}());

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,uselessCode} checked by tsc
 */
/**
 * @template T, V
 */
var Mutation = /** @class */ (function () {
    function Mutation(apollo) {
        this.apollo = apollo;
        this.client = 'default';
    }
    /**
     * @param {?=} variables
     * @param {?=} options
     * @return {?}
     */
    Mutation.prototype.mutate = /**
     * @param {?=} variables
     * @param {?=} options
     * @return {?}
     */
    function (variables, options) {
        return this.apollo.use(this.client).mutate(__assign({}, options, { variables: variables, mutation: this.document }));
    };
    Mutation.decorators = [
        { type: Injectable }
    ];
    /** @nocollapse */
    Mutation.ctorParameters = function () { return [
        { type: Apollo }
    ]; };
    return Mutation;
}());

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,uselessCode} checked by tsc
 */
/**
 * @template T, V
 */
var Subscription = /** @class */ (function () {
    function Subscription(apollo) {
        this.apollo = apollo;
        this.client = 'default';
    }
    /**
     * @param {?=} variables
     * @param {?=} options
     * @param {?=} extra
     * @return {?}
     */
    Subscription.prototype.subscribe = /**
     * @param {?=} variables
     * @param {?=} options
     * @param {?=} extra
     * @return {?}
     */
    function (variables, options, extra) {
        return this.apollo.use(this.client).subscribe(__assign({}, options, { variables: variables, query: this.document }), extra);
    };
    Subscription.decorators = [
        { type: Injectable }
    ];
    /** @nocollapse */
    Subscription.ctorParameters = function () { return [
        { type: Apollo }
    ]; };
    return Subscription;
}());

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,uselessCode} checked by tsc
 */
var SelectPipe = /** @class */ (function () {
    function SelectPipe() {
    }
    /**
     * @param {?} obj
     * @param {?=} name
     * @return {?}
     */
    SelectPipe.prototype.transform = /**
     * @param {?} obj
     * @param {?=} name
     * @return {?}
     */
    function (obj, name) {
        if (name === void 0) { name = ''; }
        if (name !== '') {
            return obj && obj.data && obj.data[name];
        }
    };
    SelectPipe.decorators = [
        { type: Pipe, args: [{
                    name: 'select',
                },] }
    ];
    return SelectPipe;
}());

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,uselessCode} checked by tsc
 */
/** @type {?} */
var PROVIDERS = [Apollo];
/** @type {?} */
var DECLARATIONS = [SelectPipe];
var ApolloModule = /** @class */ (function () {
    function ApolloModule() {
    }
    ApolloModule.decorators = [
        { type: NgModule, args: [{
                    providers: PROVIDERS,
                    declarations: DECLARATIONS,
                    exports: DECLARATIONS,
                },] }
    ];
    return ApolloModule;
}());

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,uselessCode} checked by tsc
 */

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,uselessCode} checked by tsc
 */

export { Apollo, ApolloBase, QueryRef, Query, Mutation, Subscription, SelectPipe, ApolloModule, APOLLO_OPTIONS, DECLARATIONS as ɵb, PROVIDERS as ɵa };

//# sourceMappingURL=data:application/json;charset=utf-8;base64,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